import React, { forwardRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getMarkdownComponents, safeUrlTransform, rehypePlugins } from '../utils/markdownComponents';
import { extractAssessment } from '../utils/pdfGenerator';
import { buildLessonPlanPages } from '../utils/lessonPlanPagination';
import styles from '../screens/ViewLessonPlan.module.css';
import sharedMarkdown from '../styles/markdownShared.module.css';

const LessonPlanPrintView = forwardRef(function LessonPlanPrintView(props, ref) {
  const {
    lessonTitle,
    grade,
    subject,
    concept,
    standard,
    standardDescription,
    languageLabel,
    markdown,
    includeAssessment = false,
  } = props;

  const assessmentContent = includeAssessment ? extractAssessment(markdown) : null;
  const pages = buildLessonPlanPages(markdown);
  const getSubjectDisplayName = (subjectCode) => {
    if (!subjectCode) return '';
    const normalized = String(subjectCode).toLowerCase();
    if (normalized === 'cs') return 'COMPUTER SCIENCE';
    if (normalized === 'math') return 'MATH';
    if (normalized === 'science') return 'SCIENCE';
    return String(subjectCode).toUpperCase();
  };

  const subjectLabel = getSubjectDisplayName(subject);
  const titleText = lessonTitle?.trim() || `${grade || 'Grade'} ${standard || 'Lesson Plan'}`;
  const standardLine = standardDescription
    ? `${standard || 'N/A'}: ${standardDescription}`
    : (standard || 'N/A');

  return (
    <div id="lesson-plan-print-content" ref={ref} className={styles.printContainer}>
      {/* Main Lesson Plan Content */}
      <div className={styles.a4PagesStack}>
        {pages.map((page, index) => (
          <React.Fragment key={page.key}>
            <section
              className={`${styles.a4Page} ${index < pages.length - 1 ? `page-break-after ${styles.a4PageWithBreak}` : ''}`}
            >
              {page.includeHeader && (
                <div className={styles.lessonMetaHeader}>
                  <div className={styles.lessonMetaTags}>
                    {grade && <span className={styles.metaTag}>{grade}</span>}
                    {languageLabel && <span className={styles.metaTag}>{languageLabel.toUpperCase()}</span>}
                    {subjectLabel && <span className={styles.metaTag}>{subjectLabel}</span>}
                  </div>
                  <div className={styles.lessonMetaBody}>
                    <img
                      src="/assets/KumuConnectLogos/Logo_GreenonLight.png"
                      alt="Kumu Connect"
                      className={styles.lessonMetaLogo}
                    />
                    <div className={styles.lessonMetaText}>
                      <h1 className={styles.lessonTitleCompact}>{titleText}</h1>
                      <p className={styles.lessonConceptCompact}>{concept || subjectLabel || 'Lesson'}</p>
                      <p className={styles.lessonStandardCompact}>{standardLine}</p>
                    </div>
                  </div>
                </div>
              )}
              <div className={styles.markdownContent}>
                {(page.sections || []).map((section, sectionIndex) => (
                  <React.Fragment key={section.key || `${page.key}-section-${sectionIndex}`}>
                    <section className={styles.sectionBlock}>
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={rehypePlugins}
                        urlTransform={safeUrlTransform}
                        components={getMarkdownComponents({ ...styles, ...sharedMarkdown }, { handleDrawingSpace: true, hideDrawingSpace: true })}
                      >
                        {section.markdown}
                      </ReactMarkdown>
                    </section>
                    {sectionIndex < (page.sections || []).length - 1 && (
                      <div className={styles.sectionDivider} aria-hidden="true">
                        <span className={styles.sectionDividerLine} />
                        <img
                          src="/assets/leaf_logo_white_bg.svg"
                          alt=""
                          className={styles.sectionDividerIcon}
                        />
                        <span className={styles.sectionDividerLine} />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </section>
          </React.Fragment>
        ))}
      </div>

      {/* Assessment Section (if included) */}
      {includeAssessment && assessmentContent && (
        <div className={styles.assessmentSection}>
          <div className={styles.pageBreakBefore} />
          <div className={styles.assessmentHeader}>
            <h1 className={styles.assessmentTitle}>{lessonTitle} - Assessment</h1>
            <div className={styles.assessmentMetadata}>
              <p><strong>Grade Level:</strong> {grade || 'N/A'}</p>
              <p><strong>Standard:</strong> {standard || 'N/A'}</p>
              <p><strong>Concept:</strong> {concept || 'N/A'}</p>
              <p><strong>Standard Description:</strong> {standardDescription || 'N/A'}</p>
            </div>
            <div className={styles.studentInfoBox}>
              <p><strong>Student Name:</strong> _________________________________</p>
              <p><strong>Date:</strong> _________________________________</p>
              <p><strong>Score:</strong> _________________________________</p>
            </div>
          </div>
          <div className={styles.assessmentContent}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={rehypePlugins}
              urlTransform={safeUrlTransform}
              components={getMarkdownComponents({ ...styles, ...sharedMarkdown }, { handleDrawingSpace: true, hideDrawingSpace: true })}
            >
              {assessmentContent}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
});

LessonPlanPrintView.displayName = 'LessonPlanPrintView';

export default LessonPlanPrintView;
